import React, { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useParams, useLocation } from "wouter";
import AppLayout from "@/components/layout/app-layout";
import { Button } from "@/components/ui/button";
import { ProjectWithSummary, Organization, Project } from "@/types";
import { MapPin, Calendar, Building, Trash2 } from "lucide-react";

import ProjectOverview from "@/components/project/project-overview";
import ProjectTabs from "@/components/project/project-tabs";
import ProjectDocuments from "@/components/project/project-documents";
import ProjectTeam from "@/components/project/project-team";
import ProjectWorkflowStepper from "@/components/project/project-workflow-stepper";
import ProjectNextActionCard from "@/components/project/project-next-action-card";
import ClaimsList from "@/components/claims/claims-list";
import { formatDate } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useAuth } from "@/hooks/use-auth";
import { getProjectSetupState, useDocumentAnalysis } from "@/lib/project-setup";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function ProjectDetailPage() {
  const { projectId } = useParams();
  const [location, navigate] = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [triggerReExtraction, setTriggerReExtraction] = useState(false);
  
  // Check if this is a new project
  const isNewProject = projectId === "new";
  
  // Check if user can delete projects (tenant admins and super admins)
  const canDeleteProject = user?.role === 'TenantAdmin' || user?.isSuperAdmin;
  
  // Determine which tab is active
  const isDocumentsTab = location.includes(`/projects/${projectId}/documents`);
  const isTeamTab = location.includes(`/projects/${projectId}/team`);
  
  const activeTab = isDocumentsTab ? "documents" : 
                    isTeamTab ? "team" : 
                    "claims";
  
  // Fetch project details
  const { data, isLoading, error } = useQuery<ProjectWithSummary>({
    queryKey: [`/api/projects/${projectId}`],
    enabled: !!projectId,
  });
  
  // Fix for empty project data - normalize the data structure
  const normalizedData = React.useMemo(() => {
    if (!data) return undefined;
    
    // If data.project exists, return as is
    if (data.project) return data;
    
    // Otherwise, treat the data itself as the project object and create the expected structure
    // This handles the case where the API returns just the project object directly
    return {
      project: data as unknown as Project,
      summary: {
        contractCount: 0,
        totalClaimCount: 0,
        pendingClaimCount: 0,
        totalClaimedAmount: 0
      }
    };
  }, [data]);
  
  // Fetch owner organization details
  const { data: ownerOrgData } = useQuery<Organization>({
    queryKey: [`/api/organizations/${normalizedData?.project?.ownerOrganizationId}`],
    enabled: !!normalizedData?.project?.ownerOrganizationId,
  });
  
  // Fetch contractor organization details
  const { data: contractorOrgData } = useQuery<Organization>({
    queryKey: [`/api/organizations/${normalizedData?.project?.contractorOrganizationId}`],
    enabled: !!normalizedData?.project?.contractorOrganizationId,
  });
  
  // Create formatted data that follows ProjectWithSummary structure
  const formattedData: ProjectWithSummary | undefined = isNewProject && normalizedData ? {
    project: {
      ...(normalizedData.project as Project), // Spread only the project part of data, and cast to Project
      projectName: "New Project",
      // Ensure all required fields from Project are present or provide defaults if creating a new one
      // For a truly 'new' project, many fields would be empty or have defaults.
      // This logic might need refinement based on how 'new' projects are handled.
      tenantId: normalizedData.project?.tenantId || user?.tenantId || '', // Example default
      ownerOrganizationId: normalizedData.project?.ownerOrganizationId || '', // Example default
      contractorOrganizationId: normalizedData.project?.contractorOrganizationId || '', // Example default
      status: normalizedData.project?.status || 'Planning', // Example default
    },
    summary: normalizedData.summary || {
      contractCount: 0,
      totalClaimCount: 0,
      pendingClaimCount: 0,
      totalClaimedAmount: 0
    }
  } : normalizedData;
  
  // Analyze project setup state
  const documentAnalysisQuery = useDocumentAnalysis(projectId || '');
  const documentAnalysis = documentAnalysisQuery.data || {
    hasDocuments: false,
    hasContract: false,
    contractExtracted: false,
    documentCount: 0,
    contractDocument: null
  };
  const setupState = getProjectSetupState(
    formattedData,
    documentAnalysis.hasDocuments,
    documentAnalysis.hasContract,
    documentAnalysis.contractExtracted
  );
  
  // Delete project mutation
  const deleteProjectMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("DELETE", `/api/projects/${projectId}`);
      return res;
    },
    onSuccess: () => {
      toast({
        title: "Project deleted",
        description: "The project has been successfully deleted.",
      });
      queryClient.invalidateQueries({ queryKey: ['/api/projects', user?.isSuperAdmin ? user.activeTenantContext : user?.tenantId] });
      navigate('/projects');
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: "Failed to delete project. " + error.message,
        variant: "destructive",
      });
    }
  });
  
  // Confirm and activate project mutation
  const confirmProjectMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("PUT", `/api/projects/${projectId}`, {
        status: "active"
      });
      return response.json();
    },
    onSuccess: () => {
      // Invalidate project queries to refresh the data
      queryClient.invalidateQueries({ queryKey: [`/api/projects/${projectId}`] });
      queryClient.invalidateQueries({ queryKey: ["/api/projects"] });
      
      toast({
        title: "Project activated",
        description: "Project setup is complete! You can now manage claims."
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to activate project",
        description: error.message,
        variant: "destructive"
      });
    }
  });
  
  // Handle edit project or save new project
  const handleEditProject = () => {
    if (isNewProject) {
      // Navigate to form for creating a new project
      navigate("/projects/new/edit");
    } else {
      navigate(`/projects/${projectId}/edit`);
    }
  };
  
  // Handle delete project
  const handleDeleteClick = () => {
    setShowDeleteDialog(true);
  };
  
  const handleDeleteConfirm = () => {
    deleteProjectMutation.mutate();
    setShowDeleteDialog(false);
  };
  
  // Handle confirm project
  const handleConfirmProject = () => {
    confirmProjectMutation.mutate();
  };

  // Handle re-extraction trigger
  const handleReExtract = () => {
    setTriggerReExtraction(true);
  };
  
  if (error) {
    return (
      <AppLayout>
        <div className="p-6 text-center">
          <h2 className="text-xl font-semibold">Error Loading Project</h2>
          <p className="text-muted-foreground mt-2">Failed to load project details.</p>
          <Button className="mt-4" onClick={() => navigate("/projects")}>
            Back to Projects
          </Button>
        </div>
      </AppLayout>
    );
  }
  
  return (
    <AppLayout>
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
          <div className="md:flex md:items-center md:justify-between mb-4">
            <div className="flex-1 min-w-0">
              {isLoading ? (
                <div className="bg-muted animate-pulse rounded-md h-8 w-64 mb-2" />
              ) : (
                <h1 className="text-2xl font-semibold text-neutral-800">
                  {formattedData?.project.projectName}
                </h1>
              )}
              
              <div className="mt-1 flex flex-col sm:flex-row sm:flex-wrap sm:mt-0 sm:space-x-6">
                {isLoading ? (
                  <div className="flex space-x-6">
                    <div className="bg-muted animate-pulse rounded-md h-5 w-32" />
                    <div className="bg-muted animate-pulse rounded-md h-5 w-32" />
                    <div className="bg-muted animate-pulse rounded-md h-5 w-32" />
                  </div>
                ) : (
                  <>
                    {formattedData?.project.location && (
                      <div className="mt-2 flex items-center text-sm text-neutral-500">
                        <MapPin className="flex-shrink-0 mr-1.5 text-neutral-400" size={16} />
                        <span>{formattedData.project.location}</span>
                      </div>
                    )}
                    
                    {formattedData?.project.startDate && (
                      <div className="mt-2 flex items-center text-sm text-neutral-500">
                        <Calendar className="flex-shrink-0 mr-1.5 text-neutral-400" size={16} />
                        <span>
                          {formatDate(formattedData.project.startDate)}
                          {formattedData.project.estimatedEndDate && ` - ${formatDate(formattedData.project.estimatedEndDate)}`}
                        </span>
                      </div>
                    )}
                    
                    {!isNewProject && (
                      <div className="mt-2 flex items-center text-sm text-neutral-500">
                        <Building className="flex-shrink-0 mr-1.5 text-neutral-400" size={16} />
                        <span>
                          Owner: {formattedData?.project.ownerOrganizationId 
                            ? (ownerOrgData?.name || "Loading...") 
                            : "Not selected"}
                        </span>
                      </div>
                    )}

                    {!isNewProject && (
                      <div className="mt-2 flex items-center text-sm text-neutral-500">
                        <Building className="flex-shrink-0 mr-1.5 text-neutral-400" size={16} />
                        <span>
                          Contractor: {formattedData?.project.contractorOrganizationId 
                            ? (contractorOrgData?.name || "Loading...") 
                            : "Not selected"}
                        </span>
                      </div>
                    )}
                    
                    <div className="mt-2 flex items-center text-sm">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        formattedData?.project.status === "active" ? "bg-green-100 text-green-800" :
                        formattedData?.project.status === "setup" ? "bg-blue-100 text-blue-800" :
                        "bg-neutral-100 text-neutral-800"
                      }`}>
                        {formattedData?.project.status === 'setup' ? 'Setup' : 
                         formattedData?.project.status === 'active' ? 'Active' : 
                         formattedData?.project.status === 'complete' ? 'Complete' : 
                         formattedData?.project.status}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
            
            <div className="mt-4 flex md:mt-0 md:ml-4 space-x-3">
              {/* Add New Claim button - show when not in setup mode */}
              {!isLoading && formattedData && formattedData.project.status !== 'setup' && (
                <Button 
                  variant="default"
                  onClick={() => navigate(`/projects/${projectId}/new-claim`)}
                  className="bg-primary hover:bg-primary/90"
                >
                  Add New Claim
                </Button>
              )}
              
              <Button onClick={handleEditProject}>
                Edit Project
              </Button>
              {canDeleteProject && !isNewProject && (
                <Button variant="destructive" onClick={handleDeleteClick}>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </Button>
              )}
              
              {/* Delete Confirmation Dialog */}
              <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Are you sure you want to delete this project?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. This will permanently delete the project and all associated data.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction 
                      onClick={handleDeleteConfirm}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      {deleteProjectMutation.isPending ? "Deleting..." : "Delete Project"}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
          
          {/* Project Workflow Stepper - only show during setup */}
          {!isLoading && formattedData && formattedData.project.status === 'setup' && (
            <ProjectWorkflowStepper
              currentPhase={setupState.currentPhase}
              hasDocuments={setupState.hasDocuments}
              hasContract={setupState.hasContract}
              contractExtracted={setupState.contractExtracted}
              documentsConfirmed={formattedData.project.documentsConfirmed}
            />
          )}
          
          {/* Next Action Card */}
          {!isLoading && formattedData && (
            <ProjectNextActionCard
              projectId={projectId || ""}
              currentPhase={setupState.currentPhase}
              hasDocuments={setupState.hasDocuments}
              hasContract={setupState.hasContract}
              contractExtracted={setupState.contractExtracted}
              documentsConfirmed={formattedData.project.documentsConfirmed}
              projectStatus={formattedData.project.status}
              triggerReExtraction={triggerReExtraction}
              onReExtractionStart={() => setTriggerReExtraction(false)}
            />
          )}
          
          {/* Document Upload Area - show during setup when in documents phase */}
          {!isLoading && formattedData && formattedData.project.status === 'setup' && setupState.currentPhase === 'documents' && (
            <div className="mt-6">
              <ProjectDocuments projectId={projectId || ""} />
            </div>
          )}
          
          {/* Project Overview Cards */}
          <ProjectOverview
            loading={isLoading}
            projectId={projectId}
            contractType={formattedData?.project.contractType || "Fixed Price"}
            contractAmount={formattedData?.project.contractAmount || 0}
            currency={formattedData?.project.valueCurrency || "USD"}
            projectHeadline={formattedData?.project.description || ""}
            projectSummary={formattedData?.project.projectSummary || ""}
            contractExtracted={setupState.contractExtracted}
            isSetupMode={formattedData?.project.status === 'setup'}
            onConfirm={handleConfirmProject}
            onReExtract={handleReExtract}
          />
          
          {/* Only show tabs and claims content when project is not in setup mode */}
          {formattedData?.project.status !== 'setup' && (
            <>
              {/* Project Tabs */}
              <ProjectTabs projectId={projectId || ""} currentTab={activeTab} />
              
              {/* Tab Content */}
              {!isLoading && formattedData && (
                <>
                  {/* Claims Section */}
                  {activeTab === "claims" && (
                    <ClaimsList projectId={projectId || ""} />
                  )}
                  
                  {/* Documents Section */}
                  {activeTab === "documents" && (
                    <ProjectDocuments projectId={projectId || ""} />
                  )}
                  
                  {/* Team Section */}
                  {activeTab === "team" && (
                    <ProjectTeam projectId={projectId || ""} />
                  )}
                  
                </>
              )}
            </>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
